import { Box } from '@mui/material';
import React from 'react';
import Investor from '../../models/Investor';
import ExtendedLiquid from '../../services/ExtendedLiquid';
import { EOITypography } from './EOISettingsContext';

function getSuggestedComment(investor: Investor): string | undefined {
  return (
    investor.indicativeInvestmentSuggestedComment ||
    (investor as Investor & { suggestedIndicativeInvestmentComment?: string })
      .suggestedIndicativeInvestmentComment
  );
}

function formatChangeDescription(
  current: number,
  suggested: number,
  subject: string,
): React.ReactNode {
  if (current !== suggested) {
    const direction = suggested > current ? 'increased' : 'decreased';
    return (
      <>
        {subject} has suggested that the EOI be {direction} from{' '}
        {ExtendedLiquid.currencyMillions(current, false)} to{' '}
        {ExtendedLiquid.currencyMillions(suggested, false)}
      </>
    );
  }

  return (
    <>
      {subject} has suggested an EOI of {ExtendedLiquid.currencyMillions(suggested, false)}
    </>
  );
}

function formatSelfChangeDescription(current: number, suggested: number): React.ReactNode {
  if (current !== suggested) {
    const direction = suggested > current ? 'increased' : 'decreased';
    return (
      <>
        You have suggested that the EOI be {direction} from{' '}
        {ExtendedLiquid.currencyMillions(current, false)} to{' '}
        {ExtendedLiquid.currencyMillions(suggested, false)}
      </>
    );
  }

  return <>You have suggested an EOI of {ExtendedLiquid.currencyMillions(suggested, false)}</>;
}

export const SuggestedEOISummary: React.FC<{
  investor: Investor;
  perspective: 'investor' | 'decision-maker' | 'observer';
  isCurrentAdvisorSuggestion?: boolean;
}> = ({ investor, perspective, isCurrentAdvisorSuggestion = false }) => {
  const suggested = investor.suggestedIndicativeInvestment!;
  const current = investor.indicativeInvestment;
  const comment = getSuggestedComment(investor);

  let summary: React.ReactNode;

  if (perspective === 'decision-maker') {
    summary = (
      <>
        Suggested EOI: {ExtendedLiquid.currencyMillions(suggested, false)}
        {current && current !== suggested && (
          <>
            {' '}
            ({suggested > current ? 'increase' : 'decrease'} EOI from{' '}
            {ExtendedLiquid.currencyMillions(current, false)} to{' '}
            {ExtendedLiquid.currencyMillions(suggested, false)})
          </>
        )}
      </>
    );
  } else if (perspective === 'observer') {
    if (isCurrentAdvisorSuggestion) {
      summary =
        current != null ? (
          formatSelfChangeDescription(current, suggested)
        ) : (
          <>You have suggested an EOI of {ExtendedLiquid.currencyMillions(suggested, false)}</>
        );
    } else if (investor.indicativeInvestmentSuggestedBy?.name) {
      summary = formatChangeDescription(
        current ?? suggested,
        suggested,
        investor.indicativeInvestmentSuggestedBy.name,
      );
    } else {
      summary = <>Suggested EOI: {ExtendedLiquid.currencyMillions(suggested, false)}</>;
    }
  } else if (investor.indicativeInvestmentSuggestedBy?.name) {
    summary = formatChangeDescription(
      current ?? suggested,
      suggested,
      investor.indicativeInvestmentSuggestedBy.name,
    );
  } else {
    summary = <>Suggested EOI: {ExtendedLiquid.currencyMillions(suggested, false)}</>;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <EOITypography>
        <strong>{summary}</strong>
        {perspective === 'decision-maker' && investor.indicativeInvestmentSuggestedBy && (
          <>
            <br />
            Suggested by: {investor.indicativeInvestmentSuggestedBy.name}
            {investor.indicativeInvestmentSuggestedBy.email &&
              ` (${investor.indicativeInvestmentSuggestedBy.email})`}
          </>
        )}
        {perspective === 'investor' && investor.indicativeInvestmentSuggestedBy?.email && (
          <>
            <br />
            {investor.indicativeInvestmentSuggestedBy.email}
          </>
        )}
        {perspective === 'observer' &&
          !isCurrentAdvisorSuggestion &&
          investor.indicativeInvestmentSuggestedBy?.email && (
            <>
              <br />
              {investor.indicativeInvestmentSuggestedBy.email}
            </>
          )}
        {investor.indicativeInvestmentSuggestedAt && (
          <>
            <br />
            <span style={{ fontSize: '0.875rem' }}>
              Suggested on:{' '}
              {new Date(investor.indicativeInvestmentSuggestedAt).toLocaleDateString()}
            </span>
          </>
        )}
        {comment && (
          <>
            <br />
            <br />
            <strong>Comment:</strong>
            <br />
            <span style={{ whiteSpace: 'pre-wrap' }}>{comment}</span>
          </>
        )}
      </EOITypography>
    </Box>
  );
};
